/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Decodes a COSE_Sign1 structure (RFC 8152 / RFC 9052), as carried by a C2PA
 * `c2pa.signature` box. The payload is detached in C2PA (the claim bytes are the
 * payload), so `payload` is normally null.
 */

import { decode, CborTagged } from '../cbor/decode'

export interface CoseSign1 {
    /** Raw protected-header bytes (a serialized CBOR map). Used to build the Sig_structure. */
    protectedRaw: Uint8Array
    /** Decoded protected header map (integer COSE labels). */
    protectedHeader: Map<number | string, unknown>
    /** Decoded unprotected header map (integer COSE labels and string custom labels, e.g. `sigTst2`). */
    unprotectedHeader: Map<number | string, unknown>
    /** Detached payload (null in C2PA) or inline payload bytes. */
    payload: Uint8Array | null
    /** Raw signature bytes. */
    signature: Uint8Array
}

/** Coerces a decoded CBOR map (Map for integer keys, object for string keys) into a Map preserving key types. */
function asMap (v: unknown): Map<number | string, unknown> {
    if (v instanceof Map) return v as Map<number | string, unknown>
    if (v && typeof v === 'object') {
        return new Map<number | string, unknown>(Object.entries(v as Record<string, unknown>))
    }
    return new Map()
}

/** Decodes COSE_Sign1 bytes (with or without the tag-18 wrapper). */
export function decodeSign1 (bytes: Uint8Array): CoseSign1 {
    let v: unknown = decode(bytes)
    if (v instanceof CborTagged) v = v.value
    if (!Array.isArray(v) || v.length !== 4) {
        throw new Error('not a COSE_Sign1 structure')
    }
    const [protectedRaw, unprotected, payload, signature] = v as [Uint8Array, unknown, unknown, Uint8Array]
    const protectedHeader = protectedRaw instanceof Uint8Array && protectedRaw.length > 0
        ? asMap(decode(protectedRaw))
        : new Map<number, unknown>()
    return {
        protectedRaw: protectedRaw instanceof Uint8Array ? protectedRaw : new Uint8Array(0),
        protectedHeader,
        unprotectedHeader: asMap(unprotected),
        payload: payload instanceof Uint8Array ? payload : null,
        signature: signature instanceof Uint8Array ? signature : new Uint8Array(0)
    }
}

/**
 * COSE header label 1 (alg). The algorithm MUST be in the integrity-protected
 * header (c2pa-rs reads only `sign1.protected`); a value in the unprotected
 * header is not authenticated and is ignored.
 */
export function coseAlg (sign1: CoseSign1): number | undefined {
    const a = sign1.protectedHeader.get(1)
    return typeof a === 'number' ? a : undefined
}

/**
 * The signing certificate chain (DER byte strings, leaf first). C2PA carries it
 * in the COSE `x5chain` header — usually the registered integer label 33, but
 * some producers (e.g. Truepic v1.x) use the text label `"x5chain"`.
 *
 * §14.2 (l.2259, COSE-2): "Exactly one instance of an identity credential shall
 * appear in the union of the protected and unprotected headers … structures with
 * no credentials, or two or more credentials … shall be rejected." So we count the
 * credential instances across both buckets and reject (return `[]`) when the count
 * is not exactly one — matching c2pa-rs `cert_chain_from_sign1`, which rejects a
 * chain present in both buckets (`MultipleSigningCertificateChains`).
 *
 * The end-entity cert MUST be integrity-protected (§14.5.1.2), so the protected
 * header is authoritative (integer label 33 or the text label). The unprotected
 * fallback matches ONLY the text label `"x5chain"` — never integer `33` — which is
 * exactly what c2pa-rs accepts. A single cert may be a bare bstr.
 */
export function x5chain (sign1: CoseSign1): Uint8Array[] {
    const candidates = [
        sign1.protectedHeader.get(33),
        sign1.protectedHeader.get('x5chain'),
        sign1.unprotectedHeader.get('x5chain')
    ].filter(v => v !== undefined)
    // Not exactly one credential across the union → reject (no credentials, or
    // multiple, both violate §14.2). Downstream this yields claimSignature.missing.
    if (candidates.length !== 1) return []
    const v = candidates[0]
    if (v instanceof Uint8Array) return [v]
    if (Array.isArray(v)) return v.filter((c): c is Uint8Array => c instanceof Uint8Array)
    return []
}
